import React, { useCallback } from 'react';
import { BackLink } from 'ui/BackLink';
import classnames from 'classnames';
import { ETypeValueType } from 'services/CrmApi/types/types';
import { VerticalSpace } from 'ui/VerticalSpace';
import { useRouteMatch } from 'react-router';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useResponsive } from '../hooks/useResponsive';
import { useCrmCountries } from 'services/CrmApi/queries/useCrmCountries';
import { useCrmTypeValues } from 'services/CrmApi/queries/useCrmTypeValues';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';
import { ECrmCompanyType, ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';
import { CrmCompanyTabs } from './CrmCompanyTabs/CrmCompanyTabs';
import { CompanyPageTitle } from '../components/CompanyPageTitle/CompanyPageTitle';
import { CrmCompanyEditProfile } from './CrmCompanyProfile/CrmCompanyEditProfile';
import { CrmPublishDirectoryEntry } from '../components/CrmPublishDirectoryEntry/CrmPublishDirectoryEntry';
import { useSelector } from 'react-redux';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { isCompanyProfile } from '../helpers';
import { validateCompanyForPublishing } from './helpers';
import { SubscriptionBellButton } from '../components/SubscriptionBellButton/SubscriptionBellButton';
import { IUser } from 'services/BackendApi';

interface ICrmCompanyEditRouteParams {
  companyId: string;
}

export const CrmCompanyEdit = () => {
  const match = useRouteMatch<ICrmCompanyEditRouteParams>();
  const companyId = match.params.companyId;
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();

  const isSales = useSelector(AuthSelectors.isSales);
  const isFinance = useSelector(AuthSelectors.isFinanceUser);
  const isAdmin = useSelector(AuthSelectors.isAdmin);
  const currentUser = useSelector(AuthSelectors.getCurrentUser) as IUser;
  
  const crmCountries = useCrmCountries();
  const crmTypeValuesCompanies = useCrmTypeValues(ETypeValueType.COMPANY, { enabled: true });
  const { crmDirectoryEntryResponse, directoryEntry } = useCrmDirectoryEntry(companyId);

  const contacts = [... (directoryEntry?.linkedDirectoryEntries || []) ].filter( directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.PERSON );
  const companies = [... (directoryEntry?.linkedDirectoryEntries || []) ].filter( directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.COMPANY );
  
  const messages: string[] = validateCompanyForPublishing(directoryEntry);
  const isPublished = !!directoryEntry?.publishedDate;
  const companyCanBePublished = directoryEntry?.profile.type === ECrmDirectoryEntryType.COMPANY
    && isCompanyProfile(directoryEntry?.profile)
    && [ECrmCompanyType.TRAVEL_COMPANY, ECrmCompanyType.TRANSFERS, ECrmCompanyType.ACTIVITIES, ECrmCompanyType.GROUND_SERVICES, ECrmCompanyType.HOTEL]
      .includes(directoryEntry?.profile.companyType.code as ECrmCompanyType);
  const shouldShowPublishSection = !isPublished && (isAdmin || isFinance || isSales) && companyCanBePublished;

  // Subscribers
  const isSubscribed = !!directoryEntry?.subscribers?.some(subscriber => subscriber.id === currentUser?.uuid);

  const layoutClassname = classnames('flex gap-[20px]', {
    'flex-col': isMobile || isTablet,
    'flex-row': isDesktop || isDesktopHD,
  });

  const onLinkOrUnlink = useCallback( () => {
    crmDirectoryEntryResponse.refetch();
  }, [crmDirectoryEntryResponse]);

  if (crmCountries.isPending || crmTypeValuesCompanies.isPending || crmDirectoryEntryResponse.isPending) {
    return (
      <div className="mt-[20px]">
        <LoadingBar />
      </div>
    )
  }

  if (crmCountries.isError || crmTypeValuesCompanies.isError || !directoryEntry || !directoryEntry.profile) {
    return (
      <div className="mt-[20px]">
        <ErrorBar />
      </div>
    )
  }
 
  if (directoryEntry.profile.type !== ECrmDirectoryEntryType.COMPANY || !('companyType' in directoryEntry.profile)) {
    return (
      <div className="mt-[20px]">
        <ErrorBar message="It seems this is not a company profile" />
      </div>
    )
  }

  return (
    <div className="crm-directory mx-[20px] crm-desktop:mx-[80px]">
      <div className="flex items-center justify-between">
        <BackLink type="internal" href="/crm/directory">
          Back
        </BackLink>
        <SubscriptionBellButton directoryEntry={directoryEntry} currentUser={currentUser} isSubscribed={isSubscribed} />
      </div>
      <CompanyPageTitle
        directoryEntry={directoryEntry}
        companyType={directoryEntry?.profile.companyType.value}
      />
      <VerticalSpace height="20px" />
      <div className={layoutClassname}>
        {(isMobile || isTablet) && shouldShowPublishSection && (
          <CrmPublishDirectoryEntry
            profileType={ECrmDirectoryEntryType.COMPANY}
            messages={messages}
            directoryEntryId={directoryEntry.id}
          />
        )}
        <CrmCompanyEditProfile isPublished={isPublished} directoryEntry={directoryEntry} />
        <div className="flex-1 flex flex-col gap-[20px] min-w-0">
          {(isDesktop || isDesktopHD) && shouldShowPublishSection && (
            <CrmPublishDirectoryEntry
              profileType={ECrmDirectoryEntryType.COMPANY}
              messages={messages}
              directoryEntryId={directoryEntry.id}
            />
          )}
          <CrmCompanyTabs 
            directoryEntry={directoryEntry}
            contacts={contacts}
            onLinkOrUnlink={onLinkOrUnlink}
            companies={companies}
          />
        </div>
      </div>
    </div>
  )
};
